import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

const STORAGE_KEY = 'gab_shipments_offline_queue';

/**
 * Hook de gestion d'une file de scans "hors-ligne" pour la remise de colis.
 * Chaque entrée : { id, tracking_number, delivery_code, package_index?, scanned_at }
 *
 * - Ajouter au queue avec `enqueue(entry)` quand offline (ou en secours)
 * - Le hook essaie automatiquement de flusher au retour en ligne (event online)
 * - `pending` : nombre d'entrées en attente
 * - `flush()` : forcer une resync manuelle
 */
export const useOfflineScans = () => {
  const [queue, setQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  });
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);

  const persist = useCallback((next) => {
    setQueue(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }, []);

  const enqueueDelivery = useCallback((entry) => {
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      action: 'deliver',
      scanned_at: new Date().toISOString(),
      ...entry,
    };
    persist([...queue, item]);
    toast.info(`Livraison mise en attente (hors ligne) · ${item.tracking_number}`);
    return item;
  }, [queue, persist]);

  const enqueueStatus = useCallback((entry) => {
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      action: 'status',
      scanned_at: new Date().toISOString(),
      ...entry,
    };
    persist([...queue, item]);
    toast.info(`Changement statut mis en attente (hors ligne) · ${item.tracking_number || item.shipment_id || ''}`);
    return item;
  }, [queue, persist]);

  // Rétro-compat : enqueue = enqueueDelivery
  const enqueue = enqueueDelivery;

  const flush = useCallback(async () => {
    if (queue.length === 0 || syncing) return { ok: 0, failed: 0 };
    setSyncing(true);
    let ok = 0, failed = 0;
    const remaining = [];
    for (const entry of queue) {
      try {
        const action = entry.action || 'deliver';

        if (action === 'status') {
          // Changement de statut (envoi global ou colis individuel)
          const sid = entry.shipment_id;
          if (!sid) { failed++; remaining.push(entry); continue; }
          if (typeof entry.package_index === 'number') {
            await api.put(`/shipments/${sid}/packages/${entry.package_index}/status`, { status: entry.status });
          } else {
            await api.put(`/shipments/${sid}`, { status: entry.status });
          }
          ok++;
          continue;
        }

        // Action par défaut : livraison via tracking_number → recherche puis POST
        const r = await api.get(`/shipments?search=${encodeURIComponent(entry.tracking_number)}`);
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const target = list.find(s => (s.tracking_number || '').toUpperCase() === entry.tracking_number.toUpperCase());
        if (!target) { failed++; remaining.push(entry); continue; }
        if (typeof entry.package_index === 'number') {
          await api.post(`/shipments/${target.id}/packages/${entry.package_index}/confirm-delivery`, {
            delivery_code: entry.delivery_code,
          });
        } else {
          await api.post(`/shipments/${target.id}/confirm-delivery`, {
            delivery_code: entry.delivery_code,
          });
        }
        ok++;
      } catch (e) {
        failed++;
        remaining.push(entry);
      }
    }
    persist(remaining);
    setSyncing(false);
    if (ok > 0) toast.success(`Sync : ${ok} action(s) enregistrée(s)`);
    if (failed > 0) toast.warning(`${failed} action(s) toujours en attente`);
    return { ok, failed };
  }, [queue, syncing, persist]);

  // Ecoute des changements réseau
  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [flush]);

  const remove = useCallback((id) => {
    persist(queue.filter(e => e.id !== id));
  }, [queue, persist]);

  const clear = useCallback(() => persist([]), [persist]);

  return { queue, pending: queue.length, online, syncing, enqueue, enqueueDelivery, enqueueStatus, flush, remove, clear };
};
